import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';
import ColumnHeaderProps from '@components/SchedulePage/types/ColumnHeaderProps';
import ColumnHeaderCommon from '@components/SchedulePage/ColumnHeaderCommon/ColumnHeaderCommon';
import { BlockReleaseLink } from '@components/SchedulePage/BlockReleaseLink/BlockReleaseLink';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { useSsmUiFlags } from '@utilities/hooks/useSsmUiFlags/useSsmUiFlags';
import { BlockReleaseRecommendationLink } from '@components/SchedulePage/BlockReleaseRecommendationLink/BlockReleaseRecommendationLink';
import SlotResponse from '@data/schedules/types/SlotResponse';
import { getBlockColumnHeaderDetails } from '@components/SchedulePage/utilities/getBlockColumnHeaderDetails/getBlockColumnHeaderDetails';
import AdditionalBlocks from '@components/SchedulePage/AdditionalBlocks/AdditionalBlocks';
import { checkIsBlockReleased } from '@components/SchedulePage/utilities/checkIsBlockReleased/checkIsBlockReleased';
import { isNonReleasedSurgeonBlock } from '@components/SchedulePage/utilities/blockTimeframeUtils/blockTimeframeUtils';

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			padding: theme.spacing(0.5, 1, 0.5, 1),
			backgroundColor: theme.palette.primary.dark,
			color: theme.palette.common.white,
			display: 'flex',
			flexDirection: 'column',
			borderRadius: '4px 4px 0 0',
		},
		availableTimeWrapper: {
			display: 'flex',
			alignItems: 'center',
		},
		availableTimeValue: {
			...theme.typography.subtitle1,
			fontWeight: 600,
		},
		availableTimeLabel: {
			marginLeft: theme.spacing(0.5),
			...theme.typography.body2,
		},
		blockText: {
			fontWeight: 600,
		},
		pendingReleaseText: {
			color: theme.palette.gold.main,
		},
	}),
);

const hasReleaseWithState = (state: BlockReleaseState, blocks?: Omit<SlotResponse, 'owners'>[]) => {
	return blocks?.some(block => block.release?.state === state);
};

const hasReleasableSurgeonBlock = (blocks: Omit<SlotResponse, 'owners'>[]) => {
	return (
		// Check if there are no blocks released and return early if true
		!blocks.some(checkIsBlockReleased) &&
		// Check if there is exactly one non-released surgeon block that is releasable
		blocks.filter(isNonReleasedSurgeonBlock).length === 1 &&
		// Check if the non-released surgeon block is releasable with the releasable property
		blocks.find(isNonReleasedSurgeonBlock)?.releasable
	);
};

const BlockUnreleasedColumnHeader: React.FC<ColumnHeaderProps> = ({ blockInfo, children }) => {
	const { ssmShowReleaseBlock20241205 } = useSsmUiFlags();
	const classes = useStyles();
	const location = useLocation();
	const isOnExcludedPage = location.pathname === ROUTE_CONSTANTS.BLOCK_RELEASE_REQUEST
		|| location.pathname === ROUTE_CONSTANTS.FIND_A_TIME
		|| location.pathname.includes(ROUTE_CONSTANTS.BLOCK_RELEASE_RECOMMENDATION_LIST);

	const shouldShowPendingReleaseText =
		ssmShowReleaseBlock20241205 &&
		hasReleaseWithState(BlockReleaseState.PENDING_RELEASE, blockInfo.blocks) &&
		!isOnExcludedPage;

	const shouldShowBlockReleaseRecommendationLink =
		hasReleaseWithState(BlockReleaseState.RECOMMENDED_RELEASE, blockInfo.blocks) &&
		!hasReleaseWithState(BlockReleaseState.SSM_RELEASE, blockInfo.blocks) &&
		!isOnExcludedPage &&
		!shouldShowPendingReleaseText;

	const shouldShowBlockReleaseLink =
		ssmShowReleaseBlock20241205 &&
		blockInfo?.blocks?.length &&
		hasReleasableSurgeonBlock(blockInfo.blocks) &&
		!isOnExcludedPage &&
		!shouldShowPendingReleaseText &&
		!shouldShowBlockReleaseRecommendationLink;

	const columnHeaderData= React.useMemo(() => getBlockColumnHeaderDetails(blockInfo), [blockInfo]);

	const { headerText, showUtilization, showTimeRange, primaryTimeframes, hasAdditionalBlocks, showApproximateAvailableTime } = columnHeaderData;
	return (
		<Box
			className={classes.root}
			data-field="block-unreleased-column-header"
			component="header"
		>
			<Typography variant="body2" component="h3" className={classes.blockText}>
				{headerText}
			</Typography>
			{showTimeRange && primaryTimeframes && (
				<ColumnHeaderCommon timeFrames={primaryTimeframes} />
			)}
			{showApproximateAvailableTime && (
				<Typography
					variant="body2"
					component="span"
					className={classes.availableTimeWrapper}
				>
					<Typography className={classes.availableTimeValue}>
						{blockInfo.availableTimeDisplay}
					</Typography>
					<Typography className={classes.availableTimeLabel}>approx. available</Typography>
				</Typography>
			)}
			{showUtilization && children}
			{shouldShowBlockReleaseLink && (
				<BlockReleaseLink blockInfo={blockInfo} />
			)}
			{shouldShowPendingReleaseText && (
				<Typography className={classes.pendingReleaseText} variant="body2">
					Pending Release
				</Typography>
			)}
			{shouldShowBlockReleaseRecommendationLink && (
				<BlockReleaseRecommendationLink blockInfo={blockInfo} />
			)}
			{hasAdditionalBlocks && <AdditionalBlocks blockInfo={blockInfo} />}
		</Box>
	);
};

export default BlockUnreleasedColumnHeader;
